import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Clock, User, MapPin, FileText, Trash2, CheckCircle2, Navigation, UserPlus, Package, Check } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { supabase } from '../lib/supabase';
import { triggerGcalSync } from '../lib/bookings';
import { syncLinkedTodoOnEventCompleted } from '../lib/calendarEvents';
import { useBusiness } from '../contexts/BusinessContext';
import toast from 'react-hot-toast';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import SelectResourceModal from './manage/SelectResourceModal';

interface TaskDetails {
  id: string;
  title: string;
  notes: string | null;
  status: string;
  task_number: number;
  start_date: string;
  start_time: string;
  end_date: string;
  end_time: string;
  color: string | null;
  is_mobile: boolean;
  task_address: string | null;
  todo_item_id: string | null;
  linked_todo?: {
    id: string;
    title: string;
    status: string;
    list?: {
      id: string;
      name: string;
      is_default: boolean;
    } | null;
  } | null;
  customer: {
    id: string;
    first_name: string;
    last_name: string;
    email: string | null;
    phone: string | null;
  } | null;
  resources: Array<{
    resource: {
      id: string;
      first_name: string;
      last_name: string;
      avatar_url: string | null;
    };
  }>;
  task_assets: Array<{
    asset_id: string;
    asset: {
      id: string;
      name: string;
      asset_type: string | null;
      image_url: string | null;
    };
  }>;
  created_at: string;
  completed_at: string | null;
}

interface TaskDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  taskId: string | null;
  onUpdate?: () => void;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  scheduled: { label: 'Scheduled', color: 'text-gray-700', bg: 'bg-gray-900/10' },
  in_progress: { label: 'In Progress', color: 'text-yellow-700', bg: 'bg-yellow-100' },
  completed: { label: 'Completed', color: 'text-green-700', bg: 'bg-green-100' },
};

export default function TaskDetailsModal({ isOpen, onClose, taskId, onUpdate }: TaskDetailsModalProps) {
  const { selectedBusiness } = useBusiness();
  const [task, setTask] = useState<TaskDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSelectResourceModalOpen, setIsSelectResourceModalOpen] = useState(false);
  const [assetsEnabled, setAssetsEnabled] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [editNotes, setEditNotes] = useState('');
  const [isEditingSchedule, setIsEditingSchedule] = useState(false);
  const [editedStartDate, setEditedStartDate] = useState('');
  const [editedStartTime, setEditedStartTime] = useState('');
  const [editedEndDate, setEditedEndDate] = useState('');
  const [editedEndTime, setEditedEndTime] = useState('');
  const [isSavingSchedule, setIsSavingSchedule] = useState(false);
  const [originalDuration, setOriginalDuration] = useState(0);
  const [manuallyEditedEnd, setManuallyEditedEnd] = useState(false);

  useEffect(() => {
    if (isOpen && taskId) {
      setIsAnimating(true);
      loadTask(taskId);
    }
  }, [isOpen, taskId]);

  useEffect(() => {
    if (isOpen && selectedBusiness?.id && supabase) {
      supabase.rpc('get_app_setting', {
        p_business_id: selectedBusiness.id,
        p_app_slug: 'teams',
        p_setting_key: 'assets_enabled',
        p_default_value: false
      }).then(({ data }) => {
        setAssetsEnabled(data === true || data === 'true');
      }).catch(() => setAssetsEnabled(false));
    }
  }, [isOpen, selectedBusiness?.id]);

  const loadTask = async (id: string) => {
    if (!supabase) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('calendar_tasks')
        .select(`
          *,
          customer:customers!calendar_tasks_customer_id_fkey(
            id, first_name, last_name, email, phone
          ),
          linked_todo:todo_items!calendar_tasks_todo_item_id_fkey(
            id, title, status,
            list:todo_lists!todo_items_list_id_fkey(id, name, is_default)
          ),
          resources:calendar_task_resources(
            resource:resources(id, first_name, last_name, avatar_url)
          ),
          task_assets:calendar_task_assets(
            asset_id,
            asset:assets(id, name, asset_type, image_url)
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      setTask(data);
      setEditTitle(data.title);
      setEditNotes(data.notes || '');
    } catch (error) {
      console.error('Error loading task:', error);
      toast.error('Failed to load event details');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsAnimating(false);
    setIsEditingTitle(false);
    setIsEditingNotes(false);
    setIsEditingSchedule(false);
    setTimeout(() => {
      onClose();
      setTask(null);
    }, 200);
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  const updateTask = async (updates: Record<string, any>) => {
    if (!supabase || !task) return;
    try {
      const { error } = await supabase
        .from('calendar_tasks')
        .update(updates)
        .eq('id', task.id);

      if (error) throw error;

      setTask(prev => prev ? { ...prev, ...updates } : prev);
      onUpdate?.();

      if (selectedBusiness?.id) {
        const isDelete = updates.is_deleted === true;
        triggerGcalSync(task.id, isDelete ? 'delete' : 'update', selectedBusiness.id, 'task');
      }
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update event');
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    const updates: Record<string, any> = { status: newStatus };
    if (newStatus === 'completed') {
      updates.completed_at = new Date().toISOString();
    } else if (task?.status === 'completed') {
      updates.completed_at = null;
    }
    await updateTask(updates);
    if (newStatus === 'completed' && selectedBusiness?.id && task) {
      try {
        await syncLinkedTodoOnEventCompleted(selectedBusiness.id, {
          todoItemId: task.todo_item_id,
          calendarTaskId: task.id,
        });
      } catch (err) {
        console.error('Failed to complete linked Task:', err);
      }
    }
    toast.success(`Event marked as ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
  };

  const handleSaveTitle = async () => {
    if (!editTitle.trim()) return;
    await updateTask({ title: editTitle.trim() });
    setIsEditingTitle(false);
    toast.success('Title updated');
  };

  const handleSaveNotes = async () => {
    await updateTask({ notes: editNotes.trim() || null });
    setIsEditingNotes(false);
    toast.success('Notes updated');
  };

  const handleOpenScheduleEdit = () => {
    if (!task) return;
    setEditedStartDate(task.start_date);
    setEditedStartTime(task.start_time);
    setEditedEndDate(task.end_date);
    setEditedEndTime(task.end_time);
    setManuallyEditedEnd(false);
    if (task.start_date && task.start_time && task.end_date && task.end_time) {
      const start = new Date(`${task.start_date}T${task.start_time}`);
      const end = new Date(`${task.end_date}T${task.end_time}`);
      setOriginalDuration(Math.round((end.getTime() - start.getTime()) / (1000 * 60)));
    }
    setIsEditingSchedule(true);
  };

  useEffect(() => {
    if (isEditingSchedule && !manuallyEditedEnd && originalDuration > 0 && editedStartDate && editedStartTime) {
      try {
        const newStart = new Date(`${editedStartDate}T${editedStartTime}`);
        const newEnd = new Date(newStart.getTime() + originalDuration * 60 * 1000);
        setEditedEndDate(format(newEnd, 'yyyy-MM-dd'));
        setEditedEndTime(format(newEnd, 'HH:mm'));
      } catch { /* ignore */ }
    }
  }, [editedStartDate, editedStartTime, isEditingSchedule, manuallyEditedEnd, originalDuration]);

  const scheduleValidation = (() => {
    if (!editedStartDate || !editedStartTime || !editedEndDate || !editedEndTime) {
      return { valid: false, message: 'Please fill in all date and time fields' };
    }
    try {
      const start = new Date(`${editedStartDate}T${editedStartTime}`);
      const end = new Date(`${editedEndDate}T${editedEndTime}`);
      const diffMinutes = (end.getTime() - start.getTime()) / (1000 * 60);
      if (diffMinutes <= 0) {
        return { valid: false, message: 'End date/time cannot be before or equal to start' };
      }
      return { valid: true, message: '' };
    } catch {
      return { valid: false, message: 'Invalid date or time' };
    }
  })();

  const handleSaveSchedule = async () => {
    if (!scheduleValidation.valid) return;
    setIsSavingSchedule(true);
    try {
      await updateTask({
        start_date: editedStartDate,
        start_time: editedStartTime,
        end_date: editedEndDate,
        end_time: editedEndTime,
      });
      setIsEditingSchedule(false);
      toast.success('Schedule updated');
    } catch {
      toast.error('Failed to update schedule');
    } finally {
      setIsSavingSchedule(false);
    }
  };

  const handleAddResource = async (resource: any) => {
    if (!supabase || !task) return;
    if (task.resources.some(tr => tr.resource.id === resource.id)) return;
    try {
      const { error } = await supabase
        .from('calendar_task_resources')
        .insert({ task_id: task.id, resource_id: resource.id });
      if (error) throw error;
      setTask(prev => prev ? {
        ...prev,
        resources: [...prev.resources, { resource: { id: resource.id, first_name: resource.first_name, last_name: resource.last_name, avatar_url: resource.avatar_url || null } }]
      } : prev);
      onUpdate?.();
      toast.success(`${resource.first_name} assigned to event`);
    } catch (error) {
      console.error('Error adding resource:', error);
      toast.error('Failed to assign resource');
    }
  };

  const handleRemoveResource = async (resourceId: string) => {
    if (!supabase || !task) return;
    try {
      const { error } = await supabase
        .from('calendar_task_resources')
        .delete()
        .eq('task_id', task.id)
        .eq('resource_id', resourceId);
      if (error) throw error;
      setTask(prev => prev ? {
        ...prev,
        resources: prev.resources.filter(tr => tr.resource.id !== resourceId)
      } : prev);
      onUpdate?.();
      toast.success('Resource removed from event');
    } catch (error) {
      console.error('Error removing resource:', error);
      toast.error('Failed to remove resource');
    }
  };

  const handleAddAsset = async (asset: any) => {
    if (!supabase || !task) return;
    if (task.task_assets?.some(ta => ta.asset_id === asset.id)) return;
    try {
      const { error } = await supabase
        .from('calendar_task_assets')
        .insert({ task_id: task.id, asset_id: asset.id });
      if (error) throw error;
      setTask(prev => prev ? {
        ...prev,
        task_assets: [...(prev.task_assets || []), { asset_id: asset.id, asset: { id: asset.id, name: asset.name, asset_type: asset.asset_type || null, image_url: asset.image_url || null } }]
      } : prev);
      onUpdate?.();
      toast.success(`${asset.name} assigned to event`);
    } catch (error) {
      console.error('Error adding asset:', error);
      toast.error('Failed to assign asset');
    }
  };

  const handleRemoveAsset = async (assetId: string) => {
    if (!supabase || !task) return;
    try {
      const { error } = await supabase
        .from('calendar_task_assets')
        .delete()
        .eq('task_id', task.id)
        .eq('asset_id', assetId);
      if (error) throw error;
      setTask(prev => prev ? {
        ...prev,
        task_assets: (prev.task_assets || []).filter(ta => ta.asset_id !== assetId)
      } : prev);
      onUpdate?.();
      toast.success('Asset removed from event');
    } catch (error) {
      console.error('Error removing asset:', error);
      toast.error('Failed to remove asset');
    }
  };

  const handleDelete = async () => {
    if (!supabase || !task) return;
    try {
      const { error } = await supabase
        .from('calendar_tasks')
        .update({ is_deleted: true })
        .eq('id', task.id);

      if (error) throw error;

      if (selectedBusiness?.id) {
        triggerGcalSync(task.id, 'delete', selectedBusiness.id, 'task');
      }

      toast.success('Event deleted');
      onUpdate?.();
      handleClose();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete event');
    }
  };

  const formatTime = (time: string) => {
    if (!time) return '';
    const [h, m] = time.split(':');
    const hour = parseInt(h, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${m} ${ampm}`;
  };

  const formatDate = (dateStr: string) => {
    try {
      return format(parseISO(dateStr), 'EEE, MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  if (!isOpen && !isAnimating) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  const statusConfig = task ? STATUS_CONFIG[task.status] || STATUS_CONFIG.scheduled : STATUS_CONFIG.scheduled;

  const modalContent = (
    <div
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      style={{
        zIndex: 99999,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
      }}
      onClick={handleClose}
      onTransitionEnd={handleTransitionEnd}
    >
      <div
        className={`bg-white transform transition-all duration-200 ${
          isMobile
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 99999
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          {loading || !task ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="bg-gray-50 border-b border-gray-200 px-6 py-5">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-medium text-gray-400">Event #{task.task_number}</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.color}`}>
                    {statusConfig.label}
                  </span>
                </div>
                {isEditingTitle ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveTitle();
                        if (e.key === 'Escape') { setIsEditingTitle(false); setEditTitle(task.title); }
                      }}
                      className="flex-1 text-xl font-bold text-gray-900 border border-gray-300 rounded-lg px-3 py-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      autoFocus
                    />
                    <button onClick={handleSaveTitle} className="px-3 py-1 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">Save</button>
                    <button onClick={() => { setIsEditingTitle(false); setEditTitle(task.title); }} className="px-3 py-1 text-sm font-medium text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300">Cancel</button>
                  </div>
                ) : (
                  <h2
                    className="text-xl font-bold text-gray-900 cursor-pointer hover:text-blue-600 transition-colors"
                    onClick={() => setIsEditingTitle(true)}
                  >
                    {task.title}
                  </h2>
                )}
                {task.linked_todo ? (
                  <p className="mt-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
                    Linked Task: <span className="font-semibold">{task.linked_todo.title}</span>
                    {task.linked_todo.list?.name ? (
                      <span className="ml-1.5 inline-flex items-center text-[11px] font-medium text-emerald-800 bg-emerald-100 rounded-full px-2 py-0.5 align-middle">
                        {task.linked_todo.list.name}
                      </span>
                    ) : null}
                    {task.linked_todo.status === 'completed' ? ' (completed)' : ''}
                  </p>
                ) : null}
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                {/* Status Actions */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Status</label>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                      <button
                        key={key}
                        onClick={() => handleStatusChange(key)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                          task.status === key
                            ? `${config.bg} ${config.color} ring-2 ring-offset-1 ring-current`
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                      >
                        {config.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Schedule */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Schedule</label>
                  {isEditingSchedule ? (
                    <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Start</label>
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="date"
                            value={editedStartDate}
                            onChange={(e) => setEditedStartDate(e.target.value)}
                            disabled={isSavingSchedule}
                            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:opacity-50"
                          />
                          <input
                            type="time"
                            value={editedStartTime}
                            onChange={(e) => setEditedStartTime(e.target.value)}
                            disabled={isSavingSchedule}
                            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:opacity-50"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">End</label>
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="date"
                            value={editedEndDate}
                            onChange={(e) => { setEditedEndDate(e.target.value); setManuallyEditedEnd(true); }}
                            disabled={isSavingSchedule}
                            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:opacity-50"
                          />
                          <input
                            type="time"
                            value={editedEndTime}
                            onChange={(e) => { setEditedEndTime(e.target.value); setManuallyEditedEnd(true); }}
                            disabled={isSavingSchedule}
                            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:opacity-50"
                          />
                        </div>
                      </div>

                      {!scheduleValidation.valid && editedStartDate && editedStartTime && editedEndDate && editedEndTime && (
                        <div className="flex items-center gap-2 p-2.5 bg-red-50 border border-red-200 rounded-lg">
                          <svg className="h-4 w-4 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
                          </svg>
                          <span className="text-xs text-red-700 font-medium">{scheduleValidation.message}</span>
                        </div>
                      )}

                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={handleSaveSchedule}
                          disabled={isSavingSchedule || !scheduleValidation.valid}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:scale-[0.98] transition-all font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isSavingSchedule ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Check className="w-4 h-4" />
                              Save
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => setIsEditingSchedule(false)}
                          disabled={isSavingSchedule}
                          className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 active:scale-[0.98] transition-all font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="bg-gray-50 rounded-xl p-4 cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={handleOpenScheduleEdit}
                    >
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <div className="text-[10px] font-semibold uppercase tracking-wider text-blue-600 mb-0.5">Start</div>
                          <div className="text-[12px] font-medium text-gray-900 leading-tight">
                            {format(parseISO(`${task.start_date}T${task.start_time}`), 'MMM d')} &bull; {formatTime(task.start_time)}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-0.5">End</div>
                          <div className="text-[12px] font-medium text-gray-900 leading-tight">
                            {format(parseISO(`${task.end_date}T${task.end_time}`), 'MMM d')} &bull; {formatTime(task.end_time)}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Customer */}
                {task.customer && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Customer</label>
                    <div className="bg-gray-50 rounded-xl p-4">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                          {task.customer.first_name?.[0] || ''}{task.customer.last_name?.[0] || ''}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900">
                            {task.customer.first_name} {task.customer.last_name}
                          </div>
                          {task.customer.email && (
                            <div className="text-xs text-gray-500 truncate">{task.customer.email}</div>
                          )}
                          {task.customer.phone && (
                            <div className="text-xs text-gray-500">{task.customer.phone}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Resources & Assets */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Assigned Resources</label>
                  {(task.resources && task.resources.length > 0) || (task.task_assets && task.task_assets.length > 0) ? (
                    <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                      {task.resources.map((tr) => (
                        <div key={tr.resource.id} className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {tr.resource.avatar_url ? (
                              <img src={tr.resource.avatar_url} alt="" className="h-8 w-8 rounded-full object-cover" />
                            ) : (
                              <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center text-white text-xs font-semibold">
                                {tr.resource.first_name?.[0]}{tr.resource.last_name?.[0]}
                              </div>
                            )}
                            <span className="text-sm text-gray-900">{tr.resource.first_name} {tr.resource.last_name}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveResource(tr.resource.id)}
                            className="px-2 py-1 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            Remove
                          </button>
                        </div>
                      ))}

                      {/* Assets */}
                      {task.task_assets?.map((ta) => (
                        <div key={ta.asset_id} className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
                              <Package className="h-4 w-4 text-white" />
                            </div>
                            <div>
                              <span className="text-sm text-gray-900">{ta.asset.name}</span>
                              {ta.asset.asset_type && (
                                <span className="ml-2 text-xs text-orange-600 capitalize">{ta.asset.asset_type}</span>
                              )}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveAsset(ta.asset_id)}
                            className="px-2 py-1 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            Remove
                          </button>
                        </div>
                      ))}

                      <button
                        type="button"
                        onClick={() => setIsSelectResourceModalOpen(true)}
                        className="w-full mt-2 px-3 py-2 border-2 border-dashed border-blue-300 rounded-lg text-xs font-medium text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 transition-colors flex items-center justify-center gap-1.5"
                      >
                        <UserPlus className="h-3.5 w-3.5" />
                        Add Another Resource
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setIsSelectResourceModalOpen(true)}
                      className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-all flex items-center justify-center gap-2"
                    >
                      <UserPlus className="h-4 w-4" />
                      Assign Resources
                    </button>
                  )}
                </div>

                {/* Address (mobile tasks) */}
                {task.is_mobile && task.task_address && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Location</label>
                    <div className="bg-gray-50 rounded-xl p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <span className="text-sm text-gray-900 truncate">{task.task_address}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const encoded = encodeURIComponent(task.task_address!);
                            const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encoded}`;
                            window.open(mapsUrl, '_blank');
                          }}
                          className="flex items-center gap-1.5 px-3 py-2 bg-green-500 text-white text-xs font-medium rounded-lg hover:bg-green-600 active:bg-green-700 transition-colors flex-shrink-0"
                        >
                          <Navigation className="h-3.5 w-3.5" />
                          Navigate
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Notes</label>
                  {isEditingNotes ? (
                    <div className="space-y-2">
                      <textarea
                        value={editNotes}
                        onChange={(e) => setEditNotes(e.target.value)}
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all resize-none text-sm"
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <button onClick={handleSaveNotes} className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">Save</button>
                        <button onClick={() => { setIsEditingNotes(false); setEditNotes(task.notes || ''); }} className="px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="bg-gray-50 rounded-xl p-4 cursor-pointer hover:bg-gray-100 transition-colors min-h-[60px]"
                      onClick={() => setIsEditingNotes(true)}
                    >
                      {task.notes ? (
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{task.notes}</p>
                      ) : (
                        <p className="text-sm text-gray-400 italic">Click to add notes...</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Timestamps */}
                <div className="text-xs text-gray-400 space-y-1 pt-2 border-t border-gray-100">
                  <div>Created {format(parseISO(task.created_at), 'MMM d, yyyy h:mm a')}</div>
                  {task.completed_at && (
                    <div className="flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" />
                      Completed {format(parseISO(task.completed_at), 'MMM d, yyyy h:mm a')}
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setIsDeleteModalOpen(true)}
                    className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </button>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {isDeleteModalOpen && (
        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={handleDelete}
          title="Delete Event"
          message={`Are you sure you want to delete "${task?.title}"? This action cannot be undone.`}
        />
      )}

      <SelectResourceModal
        isOpen={isSelectResourceModalOpen}
        onClose={() => setIsSelectResourceModalOpen(false)}
        onConfirm={async ({ resources, assets }) => {
          if (!task) return;
          const currentResourceIds = new Set(task.resources.map(tr => tr.resource.id));
          const nextResourceIds = new Set(resources.map(r => r.id));
          const currentAssetIds = new Set((task.task_assets || []).map(ta => ta.asset_id));
          const nextAssetIds = new Set(assets.map(a => a.id));

          for (const tr of task.resources) {
            if (!nextResourceIds.has(tr.resource.id)) {
              await handleRemoveResource(tr.resource.id);
            }
          }
          for (const resource of resources) {
            if (!currentResourceIds.has(resource.id)) {
              await handleAddResource(resource);
            }
          }

          if (assetsEnabled) {
            for (const ta of task.task_assets || []) {
              if (!nextAssetIds.has(ta.asset_id)) {
                await handleRemoveAsset(ta.asset_id);
              }
            }
            for (const asset of assets) {
              if (!currentAssetIds.has(asset.id)) {
                await handleAddAsset(asset);
              }
            }
          }
        }}
        selectedResources={task?.resources?.map(tr => tr.resource) || []}
        selectedAssetIds={task?.task_assets?.map(ta => ta.asset_id) || []}
        assetsEnabled={assetsEnabled}
        confirmLabel="Confirm"
      />
    </div>
  );

  return createPortal(modalContent, document.body);
}
